import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Settings as SettingsIcon, 
  CreditCard,
  Box,
  Mail,
  Users,
  Globe
} from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '../../ui/Button';
import  Input  from '@/components/ui/Input';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import PasswordManagement from '../settings/PasswordManagement';
import MFASetup from '../settings/MFASetup';
import SessionManagement from '../settings/SessionManagement';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('security');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/admin/settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(settings)
      });
      setSuccess('Settings saved successfully');
    } catch (error) {
      setError('Failed to save settings');
    }
  };

  if (loading) return <div>Loading...</div>;

  const tabs = [
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'platform', label: 'Platform Settings', icon: SettingsIcon },
    { id: 'billing', label: 'Billing & Pricing', icon: CreditCard },
    { id: 'integrations', label: 'Integrations', icon: Box },
    { id: 'email', label: 'Email Settings', icon: Mail },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'domains', label: 'Domains & URLs', icon: Globe }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Admin Settings</h2>
        <Button onClick={handleSave}>Save Changes</Button>
      </div>

      {(error || success) && (
        <Alert variant={error ? "destructive" : "default"}>
          <AlertDescription>{error || success}</AlertDescription>
        </Alert>
      )}

      {/* Settings Navigation */}
      <div className="flex space-x-2 border-b">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent hover:border-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Security Settings */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <PasswordManagement />
          <MFASetup />
          <SessionManagement />
        </div>
      )}

      {/* Platform Settings */}
      {activeTab === 'platform' && (
        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-1">Platform Name</label>
              <Input
                value={settings?.platformName}
                onChange={(e) => setSettings(prev => ({ ...prev, platformName: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Support Email</label>
              <Input
                type="email"
                value={settings?.supportEmail}
                onChange={(e) => setSettings(prev => ({ ...prev, supportEmail: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">File Upload Limits (MB)</label>
              <Input
                type="number"
                value={settings?.fileUploadLimit}
                onChange={(e) => setSettings(prev => ({ ...prev, fileUploadLimit: parseInt(e.target.value) }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Default Language</label>
              <select 
                className="w-full rounded-lg border-gray-300"
                value={settings?.defaultLanguage}
                onChange={(e) => setSettings(prev => ({ ...prev, defaultLanguage: e.target.value }))}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
              </select>
            </div>
          </div>
        </Card>
      )}

      {/* Billing & Pricing */}
      {activeTab === 'billing' && (
        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Pricing Plans</h3>
              {settings?.plans?.map((plan, index) => (
                <div key={index} className="mb-4 p-4 border rounded-lg">
                  <Input
                    placeholder="Plan Name"
                    value={plan.name}
                    onChange={(e) => {
                      const newPlans = [...settings.plans];
                      newPlans[index].name = e.target.value;
                      setSettings(prev => ({ ...prev, plans: newPlans }));
                    }}
                    className="mb-2"
                  />
                  <Input
                    type="number"
                    placeholder="Monthly Price"
                    value={plan.price}
                    onChange={(e) => {
                      const newPlans = [...settings.plans];
                      newPlans[index].price = parseFloat(e.target.value);
                      setSettings(prev => ({ ...prev, plans: newPlans }));
                    }}
                  />
                </div>
              ))}
              <Button 
                onClick={() => setSettings(prev => ({
                  ...prev,
                  plans: [...prev.plans, { name: '', price: 0 }]
                }))}
              >
                Add Plan
              </Button>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Payment Settings</h3>
              <div className="space-y-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings?.payment?.stripeEnabled}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      payment: { ...prev.payment, stripeEnabled: e.target.checked }
                    }))}
                  />
                  <span>Enable Stripe Payments</span>
                </label>

                {settings?.payment?.stripeEnabled && (
                  <>
                    <Input
                      placeholder="Stripe Public Key"
                      value={settings?.payment?.stripePublicKey}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        payment: { ...prev.payment, stripePublicKey: e.target.value }
                      }))}
                    />
                    <Input
                      placeholder="Stripe Secret Key"
                      type="password"
                      value={settings?.payment?.stripeSecretKey}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        payment: { ...prev.payment, stripeSecretKey: e.target.value }
                      }))}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Integrations */}
      {activeTab === 'integrations' && (
        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">AI Service Settings</h3>
              <Input
                placeholder="OpenAI API Key"
                type="password"
                value={settings?.integrations?.openaiKey}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  integrations: { ...prev.integrations, openaiKey: e.target.value }
                }))}
              />
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">SMS Service</h3>
              <div className="space-y-4">
                <select
                  className="w-full rounded-lg border-gray-300"
                  value={settings?.integrations?.smsProvider}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    integrations: { ...prev.integrations, smsProvider: e.target.value }
                  }))}
                >
                  <option value="twilio">Twilio</option>
                  <option value="messagebird">MessageBird</option>
                </select>
                <Input
                  placeholder="SMS API Key"
                  type="password"
                  value={settings?.integrations?.smsApiKey}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    integrations: { ...prev.integrations, smsApiKey: e.target.value }
                  }))}
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Email Settings */}
      {activeTab === 'email' && (
        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">SMTP Settings</h3>
              <div className="space-y-4">
                <Input
                  placeholder="SMTP Host"
                  value={settings?.email?.smtpHost}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    email: { ...prev.email, smtpHost: e.target.value }
                  }))}
                />
                <Input
                  placeholder="SMTP Port"
                  type="number"
                  value={settings?.email?.smtpPort}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    email: { ...prev.email, smtpPort: parseInt(e.target.value) }
                  }))}
                />
                <Input
                  placeholder="SMTP Username"
                  value={settings?.email?.smtpUsername}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    email: { ...prev.email, smtpUsername: e.target.value }
                  }))}
                />
                <Input
                  placeholder="SMTP Password"
                  type="password"
                  value={settings?.email?.smtpPassword}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    email: { ...prev.email, smtpPassword: e.target.value }
                  }))}
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSave}>Save Changes</Button>
      </div>
    </div>
  );
};

export default AdminSettings;